import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { useCustomerAnalytics } from "../../../../../services/hooks/useCustomerAnalytics";
import { useAppSelector } from "../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import {
  CUSTOMER_ANALYTICS_EVENT,
  PagesEnum,
} from "../../../../../utils/enums";
import { getPageLink } from "../../../../../utils/help/navigation/navigation";
import {
  getCurrentLoyaltyTier,
  sortLoyaltyTiers,
} from "../../../../../utils/loyalty/tiers";
import { TierArrowIcon } from "../../../../global/Icons/loyalty.icons";
import { LoyaltyTierCard } from "../../../../shared/LoyaltyTier/Card/LoyaltyTierCard";
import styles from "./LoyaltyTierCarousel.module.scss";

export const LoyaltyTierCarousel = () => {
  const { t, i18n } = useTranslationI18();
  const router = useRouter();
  const addCustomerAnalytics = useCustomerAnalytics();
  const trackRef = useRef<HTMLDivElement>(null);
  const customerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo.data
  );
  const tierSystem = useAppSelector((state) => state.loyaltyTierSystem.data);
  const tiers = sortLoyaltyTiers(tierSystem?.tiers);
  const currentTier = getCurrentLoyaltyTier(customerInfo, tierSystem);
  const currentIndex = tiers.findIndex(
    (tier) => tier.tierId === currentTier?.tierId
  );
  const isRtl = i18n?.dir() === "rtl";

  // Brings the member's own tier to the start of the track without
  // `scrollIntoView`, which also scrolls the page and the sticky tab bar.
  //
  // The offset is the layout distance between the first card and the target
  // card, which is exactly a scroll-snap position. A rect-derived `scrollBy`
  // is not: it can land between two snap points, and `scroll-snap-type:
  // mandatory` then snaps on to the next card and clamps at the end of the
  // track, leaving the member's card clipped off the inline-start edge.
  useEffect(() => {
    if (currentIndex < 1) {
      return;
    }

    const align = () => {
      const track = trackRef.current;
      const first = track?.children[0] as HTMLElement | undefined;
      const card = track?.children[currentIndex] as HTMLElement | undefined;

      if (!track || !first || !card) {
        return;
      }

      const offset = Math.abs(card.offsetLeft - first.offsetLeft);

      track.scrollLeft = isRtl ? -offset : offset;
    };

    const frame = requestAnimationFrame(align);

    return () => cancelAnimationFrame(frame);
  }, [currentIndex, isRtl, tiers.length]);

  if (!currentTier || !tiers.length) {
    return null;
  }

  const seeOtherTiers = () => {
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CLICKED_OTHER_TIERS);
    router.push(getPageLink(router, PagesEnum.LoyaltyTiers).href);
  };

  return (
    <section
      className={styles.carousel}
      aria-label={t("pointing-system:tiers")}
      data-testid="loyalty-tier-carousel"
    >
      <div ref={trackRef} className={styles.carousel_track}>
        {tiers.map((tier) => {
          const isCurrent = tier.tierId === currentTier.tierId;

          return (
            <LoyaltyTierCard
              key={tier.tierId}
              tier={tier}
              tiers={tiers}
              variant={isCurrent ? "current" : "preview"}
              locked={tier.tierOrder > currentTier.tierOrder}
              achieved={tier.tierOrder < currentTier.tierOrder}
              showNextTierPill={tier.tierOrder === currentTier.tierOrder + 1}
              tierProgress={isCurrent ? customerInfo?.tierProgress : undefined}
            />
          );
        })}
        {tiers.length > 1 && (
          <button
            type="button"
            className={styles.other_tiers_card}
            onClick={seeOtherTiers}
            data-testid="see-other-tiers"
          >
            <span className={styles.other_tiers_icon}>
              <TierArrowIcon />
            </span>
            <span>{t("pointing-system:see-other-tiers")}</span>
          </button>
        )}
      </div>
    </section>
  );
};
